import { UntitledIcon } from '@faceup/icons'
import { ulPlus } from '@faceup/icons/ulPlus'
import { ulTrash01 } from '@faceup/icons/ulTrash01'
import { Button, type ButtonProps, Flex } from '@faceup/ui-base'
import { type ReactNode, useContext } from 'react'
import ReactDragListView from 'react-drag-listview'
import { UiContext } from '../UiProvider/UiProvider'
import { SimpleDragListItem } from './SimpleDragListItem'

const dragListItemIdentifier = `select-form-item-option`

const defaultMinItemsCount = 0

type Item = { id: string; label: string }

export type EditableSortableListProps = {
  items: Item[]
  onMove: (fromIndex: number, toIndex: number) => void
  onAddOption: () => void
  canAddItem: boolean
  children: (data: {
    item: Item
    index: number
    config: {
      isDeletable: boolean
    }
  }) => ReactNode
  disabled?: boolean
  config?: {
    minItems?: number
  }
  underList?: ReactNode
  additionalElement?: ReactNode
}

const EditableSortableListInner = (props: EditableSortableListProps) => {
  const {
    items,
    onMove,
    onAddOption,
    canAddItem,
    children,
    disabled = false,
    config = {
      minItems: undefined,
    },
    additionalElement,
    underList,
  } = props
  const { editableSortableList } = useContext(UiContext)
  return (
    <>
      <input className='w-0 h-0 absolute invisible' />
      <Flex vertical>
        <Flex vertical gap={8}>
          <ReactDragListView
            handleSelector={`.draggable.${dragListItemIdentifier}`}
            nodeSelector={`.${dragListItemIdentifier}-item`}
            onDragEnd={(fromIndex, toIndex) => {
              // toIndex is -1 when the item is dragged outside the list
              // Skip if dropped outside the list or at the same position
              if (toIndex < 0 || fromIndex === toIndex) {
                return
              }
              onMove(fromIndex, toIndex)
            }}
            lineClassName='drag-list-line'
          >
            <Flex vertical gap={8}>
              {items.map((item, key) => (
                <SimpleDragListItem
                  key={item.id}
                  isDraggable={!disabled}
                  identifier={dragListItemIdentifier}
                >
                  {children({
                    item,
                    index: key,
                    config: {
                      isDeletable: items.length > (config?.minItems ?? defaultMinItemsCount),
                    },
                  })}
                </SimpleDragListItem>
              ))}
            </Flex>
          </ReactDragListView>
          {underList}
        </Flex>
        <Flex gap='8' align='center'>
          {canAddItem && (
            <Flex justify='left' className='mt-4'>
              <Button
                disabled={disabled}
                onClick={onAddOption}
                icon={<UntitledIcon icon={ulPlus} />}
                type='primary'
                ghost
              >
                {editableSortableList?.addItem ?? 'Add option'}
              </Button>
            </Flex>
          )}
          {additionalElement && (
            <Flex align='center' className='mt-4 h-full'>
              {additionalElement}
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  )
}

type EditableSortableItemProps = {
  children: ReactNode
  shouldDisplayDeleteButton: boolean
  deleteButtonProps: Pick<ButtonProps, 'disabled' | 'onClick'>
}

const EditableSortableItem = ({
  children,
  shouldDisplayDeleteButton,
  deleteButtonProps,
}: EditableSortableItemProps) => {
  const { editableSortableList } = useContext(UiContext)

  return (
    <Flex className='w-full'>
      <Flex align='center' gap={8} className='w-full'>
        <Flex className='w-full'>{children}</Flex>
        {shouldDisplayDeleteButton && (
          <Button
            {...deleteButtonProps}
            type='text'
            danger
            style={{ width: '32px' }}
            icon={<UntitledIcon icon={ulTrash01} />}
            label={editableSortableList?.delete ?? 'Delete'}
          />
        )}
      </Flex>
    </Flex>
  )
}

type EditableSortableListComponent = typeof EditableSortableListInner & {
  Item: typeof EditableSortableItem
}

export const EditableSortableList: EditableSortableListComponent = Object.assign(
  EditableSortableListInner,
  { Item: EditableSortableItem }
)

EditableSortableList.Item = EditableSortableItem
